import { useEffect, useContext, useRef, useState, useCallback } from 'react'
import _ from 'lodash'
import queryString from 'query-string'
import { useSessionStorage } from 'react-use'
import { getServerTime } from 'apis/utils'
import { TimeContext } from 'contexts/TimeContext'

export const useOnUnmount = (onUnmount) =>
  useEffect(() => {
    return () => onUnmount && onUnmount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

export const useOnMount = (onMount) =>
  useEffect(() => {
    onMount && onMount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

export const useTimer = () => {
  const timeContext = useContext(TimeContext)
  useEffect(() => {
    const sync = async () => {
      const begin = Date.now()
      const result = await getServerTime()
      const end = Date.now()
      const gap =
        _.get(result, 'data.timestamp', end) - Math.floor((end + begin) / 2)
      timeContext.dispatch({ type: 'SET_GAP', payload: gap })
    }
    sync()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

export function useInterval(callback, delay) {
  const savedCallback = useRef()

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current()
    }
    if (delay !== null) {
      let id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

export const useLoginUrl = () => {
  const {
    client_id,
    guid,
    client_secret,
    titlebar,
    age,
    bmi,
    gender,
    weight,
    height,
    features,
    plan_id,
  } = queryString.parse(window.location.search)

  const [planId, setPlanId] = useSessionStorage('noom:planId', plan_id)

  const getGender = () => {
    return gender && gender.toLowerCase() === 'M'.toLowerCase() ? 1 : 2
  }

  const getAge = () => {
    const ageInt = parseInt(age || '0')
    return isNaN(ageInt) || ageInt <= 0 ? 40 : age
  }

  const getBMI = () => {
    return !bmi || bmi === 'null' || bmi === null || !isFinite(bmi) ? 25 : bmi
  }

  const getHeight = () => {
    return !height || height === 'null' || height === null ? 0 : height
  }

  const getWeight = () => {
    return !weight || weight === 'null' || weight === null ? 0 : weight
  }

  useEffect(() => {
    if (client_id && client_secret && guid) {
      setPlanId(plan_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client_id, client_secret, guid, plan_id])

  return {
    clientId: client_id,
    guid,
    clientSecret: client_secret,
    titlebar,
    age: getAge(),
    bmi: getBMI(),
    gender: getGender(),
    weight: getWeight(),
    height: getHeight(),
    features: features,
    planId: planId,
    genderRaw: gender ? getGender() : '',
    ageRaw: age ? getAge() : '',
  }
}

export const useAsync = (asyncFunction, auto = true) => {
  const [pending, setPending] = useState(false)
  const [firstCall, setFirstCall] = useState(true)
  const [value, setValue] = useState(null)
  const [error, setError] = useState(null)
  const [loadSuccess, setLoadSuccess] = useState(false)

  const reSync = useCallback(
    (options) => {
      setPending(true)
      setError(null)
      setLoadSuccess(false)
      return asyncFunction(options)
        .then((response) => {
          setValue(response)
          return response
        })
        .catch((error) => {
          console.error(error)
          setError(error)
        })
        .finally(() => {
          setPending(false)
          setFirstCall(false)
          setLoadSuccess(true)
        })
    },
    [asyncFunction]
  )

  useEffect(() => {
    if (auto) {
      reSync()
    }
  }, [reSync, auto])

  return { reSync, firstCall, pending, value, error, setValue, loadSuccess }
}
